import React from "react";
import PropTypes from "prop-types";
import { Badge, Button, Col, Form, Input, Row } from "reactstrap";
import { viewShape } from "../../shared/propShapes";
import { resolveResolution } from "../../../../utils/polygonUtils";
import { isTemp, MAX_VIEWS_PER_CAMERA } from "../treeReducer";
import FieldRow, { Section } from "./FieldRow";
import ForeignKeySelect from "./ForeignKeySelect";
import PolygonEditor from "./PolygonEditor";

const ALERT_FLAGS = [
  "body_theft",
  "gesture_into_body",
  "product_in_personal_bag",
  "product_into_backpack",
  "suspicious_bag",
  "product_into_stroller",
  "client_normal_bag",
  "store_normal_bag",
  "deblistering",
  "consumption",
  "burst_shot",
  "other_suspicious",
];

function toNumber(value) {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
}

const sensitivityLabel = (s) => `Level ${s.level}${s.name ? ` — ${s.name}` : ""}`;

// Tri-state: null = use store/global default, true = enabled, false = disabled.
function TriBool({ value, onChange }) {
  const v = value === null || value === undefined ? "" : String(value);
  return (
    <Input
      type="select"
      bsSize="sm"
      className="custom-select"
      value={v}
      onChange={(e) => onChange(e.target.value === "" ? null : e.target.value === "true")}
      style={{ padding: "2px 6px", fontSize: "0.8rem" }}
    >
      <option value="">Default</option>
      <option value="true">Enabled</option>
      <option value="false">Disabled</option>
    </Input>
  );
}

TriBool.propTypes = {
  value: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

TriBool.defaultProps = { value: null };

export default function ViewForm({ view, foreignKeys, dispatch, viewsFull, canCreate, lastActiveView }) {
  const update = (patch) => dispatch({ type: "UPDATE_VIEW", publicId: view.public_id, patch });
  const set = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    update({ [field]: value });
  };
  const setNum = (field) => (e) => update({ [field]: toNumber(e.target.value) });
  const setFk = (field) => (newValue) => update({ [field]: newValue });
  const setFlag = (field) => (val) => update({ [field]: val });
  const setViewArea = (next) => update({ view_area: next });
  const setResolution = (next) => update({ resolution: next });

  // Real camera frame for this view, used by the polygon editor to detect and
  // pin edge anchors. Null when the resolution can't be resolved (the editor
  // then falls back to inferring the frame from the polygon / a manual size).
  const viewResolution = resolveResolution(view.resolution, foreignKeys.resolutions);

  const onRevert = () => dispatch({ type: "REVERT_ENTITY", publicId: view.public_id });
  const onDelete = () => dispatch({ type: "DELETE_VIEW", publicId: view.public_id });
  const onDuplicate = () => dispatch({ type: "DUPLICATE_VIEW", publicId: view.public_id });

  return (
    <Form>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h6 className="mb-0">
            🎯 View — number {view.view_number}
            {isTemp(view.public_id) && (
              <Badge color="info" className="ml-2" style={{ fontSize: "0.65rem" }}>
                new
              </Badge>
            )}
            {view.localDeleted && (
              <Badge color="danger" className="ml-2" style={{ fontSize: "0.65rem" }}>
                marked for deletion
              </Badge>
            )}
          </h6>
          <code style={{ fontSize: "0.75rem", color: "#6c757d" }}>{view.public_id}</code>
        </div>
        <Button size="sm" outline color="secondary" onClick={onRevert}>
          ↺ Revert
        </Button>
      </div>

      <Section title="Identity">
        <FieldRow label="View number" hint="Unique per camera">
          <Input type="number" bsSize="sm" value={view.view_number ?? ""} onChange={setNum("view_number")} />
        </FieldRow>
        <FieldRow label="View name">
          <Input bsSize="sm" value={view.view_name || ""} onChange={set("view_name")} />
        </FieldRow>
      </Section>

      <Section title="Inference">
        <FieldRow label="Body sensitivity">
          <ForeignKeySelect
            value={view.body_sensitivity}
            options={foreignKeys.bodySensitivities}
            getLabel={sensitivityLabel}
            onChange={setFk("body_sensitivity")}
            emptyLabel="— default —"
          />
        </FieldRow>
        <FieldRow label="Bag sensitivity">
          <ForeignKeySelect
            value={view.bag_sensitivity}
            options={foreignKeys.bagSensitivities}
            getLabel={sensitivityLabel}
            onChange={setFk("bag_sensitivity")}
            emptyLabel="— default —"
          />
        </FieldRow>
        <FieldRow label="Relevance">
          <ForeignKeySelect value={view.relevance} options={foreignKeys.relevances} onChange={setFk("relevance")} />
        </FieldRow>
      </Section>

      <Section title="View area (polygons)">
        {/* Key by view so switching views remounts the editor — its local row
            state must not leak from the previously selected view. */}
        <PolygonEditor
          key={view.public_id}
          value={view.view_area}
          resolution={viewResolution}
          onChange={setViewArea}
          onResolutionChange={setResolution}
        />
      </Section>

      <Section title="State">
        <FieldRow label="Active" hint={lastActiveView ? "A camera must keep at least one active view" : undefined}>
          <Input type="checkbox" checked={!!view.is_active} onChange={set("is_active")} disabled={lastActiveView} />
        </FieldRow>
      </Section>

      <Section title="Alert flags (12)">
        <Row>
          {ALERT_FLAGS.map((flag) => (
            <Col key={flag} md="6" className="mb-1">
              <div className="d-flex align-items-center" style={{ gap: 6 }}>
                <div style={{ fontSize: "0.8rem", flex: 1 }}>{flag.replace(/_/g, " ")}</div>
                <div style={{ width: 110 }}>
                  <TriBool value={view[flag]} onChange={setFlag(flag)} />
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </Section>

      <div className="d-flex justify-content-between mt-3">
        {/* The empty span keeps Delete right-aligned in the justify-between row when the
            creation affordance is hidden from a non-editor. */}
        {canCreate ? (
          <Button
            size="sm"
            outline
            color="secondary"
            onClick={onDuplicate}
            disabled={view.localDeleted || viewsFull}
            title={viewsFull ? `A camera can have at most ${MAX_VIEWS_PER_CAMERA} views` : undefined}
          >
            Duplicate view
          </Button>
        ) : (
          <span />
        )}
        <Button
          size="sm"
          outline
          color="danger"
          onClick={onDelete}
          disabled={view.localDeleted || lastActiveView}
          title={lastActiveView ? "A camera must keep at least one active view" : undefined}
        >
          {view.localDeleted ? "Marked for deletion" : "Delete view"}
        </Button>
      </div>
    </Form>
  );
}

ViewForm.propTypes = {
  /** Whether the user may create entities — the `editorRole` gate carried by the legacy recorders. */
  canCreate: PropTypes.bool.isRequired,
  view: viewShape.isRequired,
  foreignKeys: PropTypes.shape({
    bodySensitivities: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number })).isRequired,
    bagSensitivities: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number })).isRequired,
    relevances: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number })).isRequired,
    resolutions: PropTypes.arrayOf(
      PropTypes.shape({ id: PropTypes.number, width: PropTypes.number, height: PropTypes.number })
    ).isRequired,
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  viewsFull: PropTypes.bool,
  /** True when this is the camera's only active view — deactivation is then barred. */
  lastActiveView: PropTypes.bool,
};

ViewForm.defaultProps = { viewsFull: false, lastActiveView: false };
